import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {useLinkResolver} from 'prismicio'
import type {CtaV2SliceSmall} from 'prismicio-types'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {RatingsSection} from 'slices/HeroV2/RatingsSection'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {getButtonsData} from 'src/common/helpers'
import type {Context} from 'src/common/types'

type SmallVariationProps = {
  slice: CtaV2SliceSmall & {
    slice_type: string
    variation: string
  }
  index: number
  context: Context
}

export const SmallVariation = ({slice, index, context: {pageUid, buttonsData}}: SmallVariationProps) => {
  const {linkResolver} = useLinkResolver()
  const isImageAvaiable = isFilled.image(slice.primary.image)

  const {primaryButtonData, secondaryButtonData} = getButtonsData({
    buttonsData,
    primaryButton: slice.primary.primary_button,
    secondaryButton: slice.primary.secondary_button,
  })

  return (
    <Container
      backgroundColor={slice.primary.background_color || 'Grey/100'}
      className="!py-12 px-5 md:!py-15 md:px-10 lg:px-5 xl:px-0"
      data-slice-variation={slice.variation}
    >
      <div className="w-full max-w-[1200px] overflow-hidden rounded-lg bg-green-dark md:flex">
        <div
          className={cn('flex w-full flex-col justify-center', {
            'items-center gap-6 p-12 md:py-16 md:px-15': !isImageAvaiable,
            'p-6 md:p-12 gap-8': isImageAvaiable,
          })}
        >
          <div className="flex gap-3">
            <RatingsSection
              ratings={slice.primary.ratings}
              type={slice.primary.ratings_type}
              version="v2"
              darkBackground
            />
          </div>
          <h2
            className={cn('text-32 font-medium leading-10 text-moss-white', {
              'text-center': !isImageAvaiable,
            })}
          >
            {slice.primary.title}
          </h2>
          {primaryButtonData && (
            <ButtonV2
              variant="primary"
              trackingData={{
                mktgId: primaryButtonData.marketing_id || 'cta-button',
                location: `component-${index}-cta-small`,
                dataType: 'primary-button',
                pageName: pageUid,
              }}
              href={linkResolver(primaryButtonData.link)}
              className="w-fit"
            >
              {primaryButtonData.label}
            </ButtonV2>
          )}
          {secondaryButtonData && (
            <ButtonV2
              variant="primary"
              trackingData={{
                mktgId: secondaryButtonData.marketing_id || 'cta-button',
                location: `component-${index}-cta-small`,
                dataType: 'secondary-button',
                pageName: pageUid,
              }}
              href={linkResolver(secondaryButtonData.link)}
              className="w-fit"
            >
              {secondaryButtonData.label}
            </ButtonV2>
          )}
        </div>
        {isImageAvaiable && <PrismicNextImage field={slice.primary.image} className="w-[296px] max-md:hidden" />}
      </div>
    </Container>
  )
}
